import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Layout, Button, Space, Toast, Card, InputNumber, Modal, Input, Switch } from '@douyinfe/semi-ui';
import { IconImage } from '@douyinfe/semi-icons';
import { ColorPicker } from '../../components/ColorPicker';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ImageSelector } from '../../components/ImageSelector';
import DroppableCanvas from './DroppableCanvas';
import styles from './index.module.scss';
import { copyToClipboard } from '../../utils/clipboard';

const { Header, Sider, Content } = Layout;

const PosterEditor = () => {
    const [posterConfig, setPosterConfig] = useState({
        type: "poster",
        image: "",
        bg: "#ffffff",
        w: 750,
        h: 1334,
        content: []
    });
    const [selectedElementIndex, setSelectedElementIndex] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [canvasLocked, setCanvasLocked] = useState(false);
    const [aspectRatioLocked, setAspectRatioLocked] = useState({}); // 存储每个图片元素的比例锁定状态
    const [originalAspectRatios, setOriginalAspectRatios] = useState({}); // 存储每个图片元素的原始宽高比
    const canvasRef = useRef(null);
    const jsonTextareaRef = useRef(null);
    const [jsonString, setJsonString] = useState("");

    // 获取图片尺寸的函数
    const getImageDimensions = (imageUrl) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                resolve({ width: img.naturalWidth, height: img.naturalHeight });
            };
            img.onerror = reject;
            img.src = imageUrl;
        });
    };

    // 计算宽高比
    const calculateAspectRatio = (width, height) => {
        return width / height;
    };

    // 根据宽高比计算新的尺寸
    const calculateSizeByAspectRatio = (width, height, aspectRatio, isWidthChange) => {
        if (isWidthChange) {
            return { width, height: width / aspectRatio };
        } else {
            return { width: height * aspectRatio, height };
        }
    };

    // 自动调整画布尺寸为背景图片尺寸
    const autoResizeCanvasToImage = async (imageUrl) => {
        if (canvasLocked) {
            Toast.info('画布已锁定，不会自动调整尺寸');
            return;
        }

        try {
            const dimensions = await getImageDimensions(imageUrl);
            setPosterConfig(prev => ({
                ...prev,
                w: dimensions.width,
                h: dimensions.height
            }));
            Toast.success(`画布已自动调整为图片尺寸: ${dimensions.width} × ${dimensions.height}`);
        } catch (error) {
            console.error('Failed to get image dimensions:', error);
            Toast.error('获取图片尺寸失败');
        }
    };

    // 设置图片元素为原图尺寸
    const setImageElementToOriginalSize = async (elementIndex) => {
        const element = posterConfig.content[elementIndex];
        if (!element || !element.i) {
            Toast.warning('请先选择图片');
            return;
        }

        try {
            const dimensions = await getImageDimensions(element.i);
            const aspectRatio = calculateAspectRatio(dimensions.width, dimensions.height);

            // 保存原始宽高比
            setOriginalAspectRatios(prev => ({
                ...prev,
                [elementIndex]: aspectRatio
            }));

            handleElementChange(elementIndex, {
                w: dimensions.width,
                h: dimensions.height
            });
            Toast.success(`图片已设置为原图尺寸: ${dimensions.width} × ${dimensions.height}`);
        } catch (error) {
            console.error('Failed to get image dimensions:', error);
            Toast.error('获取图片尺寸失败');
        }
    };

    // 合成海报函数
    const generatePoster = async () => {
        setIsGenerating(true);
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // 设置画布尺寸
            canvas.width = posterConfig.w;
            canvas.height = posterConfig.h;

            // 绘制背景
            if (posterConfig.image) {
                // 如果有背景图片，先加载图片
                const bgImage = new Image();
                bgImage.crossOrigin = 'anonymous';

                await new Promise((resolve, reject) => {
                    bgImage.onload = resolve;
                    bgImage.onerror = reject;
                    bgImage.src = posterConfig.image;
                });

                ctx.drawImage(bgImage, 0, 0, posterConfig.w, posterConfig.h);
            } else {
                // 否则使用背景颜色
                ctx.fillStyle = posterConfig.bg;
                ctx.fillRect(0, 0, posterConfig.w, posterConfig.h);
            }

            // 绘制内容元素
            for (const element of posterConfig.content) {
                if (element.i !== undefined) {
                    // 绘制图片元素
                    if (element.i) {
                        const img = new Image();
                        img.crossOrigin = 'anonymous';

                        await new Promise((resolve, reject) => {
                            img.onload = resolve;
                            img.onerror = reject;
                            img.src = element.i;
                        });

                        // 如果有圆角，需要特殊处理
                        if (element.r && element.r > 0) {
                            ctx.save();
                            ctx.beginPath();
                            // 兼容性处理：如果支持roundRect则使用，否则绘制圆角矩形
                            if (ctx.roundRect) {
                                ctx.roundRect(element.x, element.y, element.w, element.h, element.r);
                            } else {
                                // 手动绘制圆角矩形
                                const x = element.x;
                                const y = element.y;
                                const w = element.w;
                                const h = element.h;
                                const r = element.r;

                                ctx.moveTo(x + r, y);
                                ctx.lineTo(x + w - r, y);
                                ctx.quadraticCurveTo(x + w, y, x + w, y + r);
                                ctx.lineTo(x + w, y + h - r);
                                ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
                                ctx.lineTo(x + r, y + h);
                                ctx.quadraticCurveTo(x, y + h, x, y + h - r);
                                ctx.lineTo(x, y + r);
                                ctx.quadraticCurveTo(x, y, x + r, y);
                            }
                            ctx.clip();
                            ctx.drawImage(img, element.x, element.y, element.w, element.h);
                            ctx.restore();
                        } else {
                            ctx.drawImage(img, element.x, element.y, element.w, element.h);
                        }
                    }
                } else if (element.t !== undefined) {
                    // 绘制文字元素
                    ctx.save();
                    ctx.font = `${element.f || 'normal'} ${element.s || 16}px Arial, sans-serif`;
                    ctx.fillStyle = element.c || '#000000';
                    ctx.textAlign = element.a || 'left';

                    // 处理文字换行
                    const text = element.t || '';
                    const maxWidth = element.w;
                    const fontSize = element.s || 16;
                    const lineHeight = fontSize * 1.2;

                    // 按字符分割，支持中文换行
                    const chars = text.split('');
                    const lines = [];
                    let currentLine = '';

                    for (let i = 0; i < chars.length; i++) {
                        const testLine = currentLine + chars[i];
                        const metrics = ctx.measureText(testLine);

                        if (metrics.width > maxWidth && currentLine !== '') {
                            lines.push(currentLine);
                            currentLine = chars[i];
                        } else {
                            currentLine = testLine;
                        }
                    }

                    if (currentLine) {
                        lines.push(currentLine);
                    }

                    // 计算文字总高度
                    const totalTextHeight = lines.length * lineHeight;

                    // 计算垂直居中的起始Y坐标
                    const containerHeight = element.h;
                    const startY = element.y + (containerHeight - totalTextHeight) / 2 + fontSize;

                    // 绘制每一行文字
                    let y = startY;
                    for (const line of lines) {
                        // 根据对齐方式调整x坐标
                        let x = element.x;
                        if (element.a === 'center') {
                            const metrics = ctx.measureText(line);
                            x = element.x + (maxWidth - metrics.width) / 2;
                        } else if (element.a === 'right') {
                            const metrics = ctx.measureText(line);
                            x = element.x + maxWidth - metrics.width;
                        }

                        ctx.fillText(line, x, y);
                        y += lineHeight;
                    }
                    ctx.restore();
                }
            }

            // 转换为图片并下载
            canvas.toBlob((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `poster_${Date.now()}.png`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                Toast.success('海报已生成并下载');
            }, 'image/png');

        } catch (error) {
            console.error('生成海报失败:', error);
            Toast.error('生成海报失败: ' + error.message);
        } finally {
            setIsGenerating(false);
        }
    };

    useEffect(() => {
        // 实时更新配置JSON
        const configString = JSON.stringify(posterConfig, null, 2);
        setJsonString(configString);
    }, [posterConfig]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Delete' && selectedElementIndex !== null) {
                handleDeleteElement(selectedElementIndex);
            }

            // 方向键调整位置
            if (selectedElementIndex !== null && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
                event.preventDefault();
                const step = event.shiftKey ? 10 : 1; // Shift + 方向键移动10像素，否则移动1像素

                const selectedElement = posterConfig.content[selectedElementIndex];
                if (!selectedElement) return;

                let newX = selectedElement.x;
                let newY = selectedElement.y;

                switch (event.key) {
                    case 'ArrowUp':
                        newY = Math.max(0, selectedElement.y - step);
                        break;
                    case 'ArrowDown':
                        newY = Math.min(posterConfig.h - selectedElement.h, selectedElement.y + step);
                        break;
                    case 'ArrowLeft':
                        newX = Math.max(0, selectedElement.x - step);
                        break;
                    case 'ArrowRight':
                        newX = Math.min(posterConfig.w - selectedElement.w, selectedElement.x + step);
                        break;
                }

                handleElementChange(selectedElementIndex, { x: newX, y: newY });
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedElementIndex, posterConfig.content, posterConfig.w, posterConfig.h]);

    const handleConfigChange = (newConfigString) => {
        try {
            const newConfig = JSON.parse(newConfigString);
            setPosterConfig(newConfig);
        } catch (error) {
            Toast.error('配置格式不正确');
            console.error('Error parsing poster config:', error);
        }
    };

    // 添加图片元素
    const handleAddImage = () => {
        const newImage = {
            i: "", // 图片URL
            x: 50,
            y: 50,
            w: 200,
            h: 200,
            r: 0 // 圆角
        };
        setPosterConfig(prev => {
            const newIndex = prev.content.length;
            setSelectedElementIndex(newIndex);
            return {
                ...prev,
                content: [...prev.content, newImage]
            };
        });
    };

    // 添加文字元素
    const handleAddText = () => {
        const newText = {
            t: "请输入文字",
            x: 50,
            y: 50,
            w: 200, // 宽度
            h: 50,  // 高度
            c: "#000000", // 颜色
            s: 16, // 文字大小
            f: "normal", // 文字粗细
            a: "left" // 对齐方式
        };
        setPosterConfig(prev => {
            const newIndex = prev.content.length;
            setSelectedElementIndex(newIndex);
            return {
                ...prev,
                content: [...prev.content, newText]
            };
        });
    };

    // 移动元素到顶层
    const moveElementToTop = (index) => {
        if (index === posterConfig.content.length - 1) {
            Toast.info('元素已在顶层');
            return;
        }

        setPosterConfig(prev => {
            const newContent = [...prev.content];
            const element = newContent.splice(index, 1)[0];
            newContent.push(element);
            return {
                ...prev,
                content: newContent
            };
        });

        // 更新选中的索引
        setSelectedElementIndex(posterConfig.content.length - 1);
        Toast.success('已移动到顶层');
    };

    // 移动元素到底层
    const moveElementToBottom = (index) => {
        if (index === 0) {
            Toast.info('元素已在底层');
            return;
        }

        setPosterConfig(prev => {
            const newContent = [...prev.content];
            const element = newContent.splice(index, 1)[0];
            newContent.unshift(element);
            return {
                ...prev,
                content: newContent
            };
        });

        // 更新选中的索引
        setSelectedElementIndex(0);
        Toast.success('已移动到底层');
    };

    // 删除元素
    const handleDeleteElement = (index) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除此元素吗？',
            onOk: () => {
                setPosterConfig(prev => ({
                    ...prev,
                    content: prev.content.filter((_, i) => i !== index)
                }));

                // 清理比例锁定状态
                setAspectRatioLocked(prev => {
                    const newState = { ...prev };
                    delete newState[index];
                    // 重新索引
                    const reindexed = {};
                    Object.keys(newState).forEach(key => {
                        const keyNum = parseInt(key);
                        if (keyNum > index) {
                            reindexed[keyNum - 1] = newState[key];
                        } else {
                            reindexed[keyNum] = newState[key];
                        }
                    });
                    return reindexed;
                });

                setOriginalAspectRatios(prev => {
                    const newState = { ...prev };
                    delete newState[index];
                    // 重新索引
                    const reindexed = {};
                    Object.keys(newState).forEach(key => {
                        const keyNum = parseInt(key);
                        if (keyNum > index) {
                            reindexed[keyNum - 1] = newState[key];
                        } else {
                            reindexed[keyNum] = newState[key];
                        }
                    });
                    return reindexed;
                });

                if (selectedElementIndex === index) {
                    setSelectedElementIndex(null);
                } else if (selectedElementIndex > index) {
                    setSelectedElementIndex(selectedElementIndex - 1);
                }
            },
        });
    };

    // 更新元素属性（支持比例锁定）
    const handleElementChange = useCallback((index, changes) => {
        setPosterConfig(prev => ({
            ...prev,
            content: prev.content.map((item, i) => {
                if (i === index) {
                    const newItem = { ...item, ...changes };

                    // 如果是图片元素且启用了比例锁定，且修改了宽度或高度
                    if (item.i !== undefined && aspectRatioLocked[index] && originalAspectRatios[index]) {
                        const aspectRatio = originalAspectRatios[index];

                        if (changes.w !== undefined && changes.h === undefined) {
                            // 只修改了宽度，根据比例计算高度
                            const newHeight = Math.round(changes.w / aspectRatio);
                            newItem.h = newHeight;
                        } else if (changes.h !== undefined && changes.w === undefined) {
                            // 只修改了高度，根据比例计算宽度
                            const newWidth = Math.round(changes.h * aspectRatio);
                            newItem.w = newWidth;
                        }
                    }

                    return newItem;
                }
                return item;
            })
        }));
    }, [aspectRatioLocked, originalAspectRatios]);

    // 处理图片URL变化时保存原始宽高比
    const handleImageUrlChange = useCallback(async (elementIndex, imageUrl) => {
        if (imageUrl) {
            try {
                const dimensions = await getImageDimensions(imageUrl);
                const aspectRatio = calculateAspectRatio(dimensions.width, dimensions.height);

                setOriginalAspectRatios(prev => ({
                    ...prev,
                    [elementIndex]: aspectRatio
                }));

                // 如果启用了比例锁定，自动设置尺寸
                if (aspectRatioLocked[elementIndex]) {
                    handleElementChange(elementIndex, {
                        i: imageUrl,
                        w: dimensions.width,
                        h: dimensions.height
                    });
                } else {
                    handleElementChange(elementIndex, { i: imageUrl });
                }
            } catch (error) {
                console.error('Failed to get image dimensions:', error);
                handleElementChange(elementIndex, { i: imageUrl });
            }
        } else {
            handleElementChange(elementIndex, { i: imageUrl });
        }
    }, [aspectRatioLocked, handleElementChange]);

    // 切换比例锁定状态
    const toggleAspectRatioLock = useCallback((elementIndex) => {
        setAspectRatioLocked(prev => {
            const newState = { ...prev, [elementIndex]: !prev[elementIndex] };

            // 如果启用了比例锁定且没有原始宽高比，尝试获取
            if (newState[elementIndex] && !originalAspectRatios[elementIndex]) {
                const element = posterConfig.content[elementIndex];
                if (element && element.i) {
                    getImageDimensions(element.i).then(dimensions => {
                        const aspectRatio = calculateAspectRatio(dimensions.width, dimensions.height);
                        setOriginalAspectRatios(prev => ({
                            ...prev,
                            [elementIndex]: aspectRatio
                        }));
                    }).catch(error => {
                        console.error('Failed to get image dimensions:', error);
                    });
                }
            }

            return newState;
        });
    }, [originalAspectRatios, posterConfig.content]);

    // 更新海报基础属性
    const handlePosterChange = useCallback((changes) => {
        setPosterConfig(prev => ({
            ...prev,
            ...changes
        }));
    }, []);

    const selectedElement = selectedElementIndex !== null ? posterConfig.content[selectedElementIndex] : null;

    return (
        <DndProvider backend={HTML5Backend}>
            <Layout className={styles.editorContainer}>
                <Header className={styles.header}>
                    <Space>
                        <Button onClick={handleAddImage}>添加图片</Button>
                        <Button onClick={handleAddText}>添加文字</Button>
                        <Button type="warning" icon={<IconImage />} onClick={generatePoster} disabled={isGenerating}>
                            {isGenerating ? '生成中...' : '合成海报'}
                        </Button>
                    </Space>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        <span>💡 提示：</span>
                        <span>拖拽移动 | 拖拽四角调整大小 | 方向键精确调整 | Shift+方向键快速移动</span>
                    </div>
                </Header>
                <Layout>
                    <Content className={styles.content}>
                        <div className={styles.canvasContainer}>
                            <DroppableCanvas
                                config={posterConfig}
                                selectedElementIndex={selectedElementIndex}
                                onElementSelect={setSelectedElementIndex}
                                onElementChange={handleElementChange}
                                onPosterChange={handlePosterChange}
                                aspectRatioLocked={aspectRatioLocked}
                                originalAspectRatios={originalAspectRatios}
                            />
                        </div>
                        <Card
                            title="海报配置JSON"
                            headerStyle={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            style={{ marginTop: '20px', width: '100%' }}
                        >
                            <Space style={{ marginBottom: '16px' }}>
                                <Button
                                    icon="copy"
                                    onClick={() => {
                                        copyToClipboard(jsonString).then(() => {
                                            Toast.success('配置已复制');
                                        }).catch(err => {
                                            console.error('Failed to copy text: ', err);
                                            Toast.error('复制失败');
                                        });
                                    }}
                                >
                                    复制配置
                                </Button>
                                <Button
                                    icon="check"
                                    onClick={() => {
                                        try {
                                            handleConfigChange(jsonString);
                                            Toast.success('配置已应用');
                                        } catch (err) {
                                            console.error('Failed to apply config: ', err);
                                            Toast.error('应用配置失败');
                                        }
                                    }}
                                >
                                    应用配置
                                </Button>
                            </Space>
                            <textarea
                                ref={jsonTextareaRef}
                                value={jsonString}
                                onChange={(e) => setJsonString(e.target.value)}
                                style={{
                                    width: '100%',
                                    height: '400px',
                                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                    fontSize: '14px',
                                    lineHeight: '1.4',
                                    padding: '12px',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '6px',
                                    resize: 'vertical',
                                    backgroundColor: '#fafafa'
                                }}
                                placeholder="海报配置JSON"
                            />
                        </Card>
                    </Content>
                    <Sider className={styles.sider}>
                        <Card title="元素列表(点击快捷选择)" style={{ marginBottom: '16px' }}>
                            <div style={{ padding: '20px' }}>
                                {posterConfig.content.length === 0 ? (
                                    <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                                        暂无元素，点击上方按钮添加
                                    </div>
                                ) : (
                                    <div className={styles.elementList}>
                                        {posterConfig.content.map((element, index) => (
                                            <div
                                                key={index}
                                                className={`${styles.elementItem} ${selectedElementIndex === index ? styles.elementItemSelected : ''}`}
                                                onClick={() => setSelectedElementIndex(index)}
                                            >
                                                <div className={styles.elementPreview}>
                                                    {element.i ? (
                                                        <img
                                                            src={element.i}
                                                            alt="图片元素"
                                                            className={styles.elementThumbnail}
                                                        />
                                                    ) : (
                                                        <div className={styles.textElementPreview}>
                                                            T
                                                        </div>
                                                    )}
                                                </div>
                                                <div className={styles.elementInfo}>
                                                    <div className={styles.elementName}>
                                                        {element.i ? '图片元素' : '文字元素'}
                                                        <span className={styles.elementIndex}>#{index + 1}</span>
                                                    </div>
                                                    <div className={styles.elementDetails}>
                                                        {element.i ? (
                                                            `${element.w}×${element.h}`
                                                        ) : (
                                                            element.t ? `"${element.t.substring(0, 10)}${element.t.length > 10 ? '...' : ''}"` : '空文字'
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={styles.elementActions}>
                                                    <Button
                                                        type="danger"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteElement(index);
                                                        }}
                                                    >
                                                        删除
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Card>
                        {selectedElement && (
                            <Card title="当前元素设置">
                                <div style={{ padding: '20px' }}>
                                    {selectedElement.i !== undefined ? (
                                        // 图片元素设置
                                        <div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>图片URL:</label>
                                                <ImageSelector
                                                    value={selectedElement.i}
                                                    onChange={value => handleImageUrlChange(selectedElementIndex, value)}
                                                />
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>宽度:</label>
                                                <InputNumber
                                                    value={selectedElement.w}
                                                    onChange={value => handleElementChange(selectedElementIndex, { w: value })}
                                                    style={{ width: '100%' }}
                                                    precision={0}
                                                    min={1}
                                                />
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>高度:</label>
                                                <InputNumber
                                                    value={selectedElement.h}
                                                    onChange={value => handleElementChange(selectedElementIndex, { h: value })}
                                                    style={{ width: '100%' }}
                                                    precision={0}
                                                    min={1}
                                                />
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>圆角:</label>
                                                <InputNumber
                                                    value={selectedElement.r}
                                                    onChange={value => handleElementChange(selectedElementIndex, { r: value })}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            {selectedElement.i && (
                                                <div style={{ marginBottom: '16px' }}>
                                                    <Button
                                                        type="secondary"
                                                        size="small"
                                                        onClick={() => setImageElementToOriginalSize(selectedElementIndex)}
                                                        style={{ width: '100%' }}
                                                    >
                                                        设为原图尺寸
                                                    </Button>
                                                </div>
                                            )}
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>比例锁定:</label>
                                                <Switch
                                                    checked={aspectRatioLocked[selectedElementIndex] || false}
                                                    onChange={() => toggleAspectRatioLock(selectedElementIndex)}
                                                    style={{ marginLeft: '10px' }}
                                                />
                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                    锁定宽高比，调整宽度或高度时保持比例
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        // 文字元素设置
                                        <div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>文字内容:</label>
                                                <Input
                                                    key={`text-input-${selectedElementIndex}`}
                                                    value={selectedElement.t}
                                                    onChange={value => handleElementChange(selectedElementIndex, { t: value })}
                                                    style={{ width: '100%' }}
                                                    autoFocus={false}
                                                />
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>文字颜色:</label>
                                                <ColorPicker
                                                    value={selectedElement.c}
                                                    onChange={value => handleElementChange(selectedElementIndex, { c: value })}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>文字大小:</label>
                                                <InputNumber
                                                    value={selectedElement.s}
                                                    onChange={value => handleElementChange(selectedElementIndex, { s: value })}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>文字粗细:</label>
                                                <select
                                                    value={selectedElement.f || "normal"}
                                                    onChange={e => handleElementChange(selectedElementIndex, { f: e.target.value })}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #d9d9d9', borderRadius: '6px' }}
                                                >
                                                    <option value="normal">正常</option>
                                                    <option value="bold">粗体</option>
                                                    <option value="100">极细</option>
                                                    <option value="200">细体</option>
                                                    <option value="300">轻体</option>
                                                    <option value="400">正常</option>
                                                    <option value="500">中等</option>
                                                    <option value="600">半粗体</option>
                                                    <option value="700">粗体</option>
                                                    <option value="800">特粗</option>
                                                    <option value="900">黑体</option>
                                                </select>
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>对齐方式:</label>
                                                <select
                                                    value={selectedElement.a || "left"}
                                                    onChange={e => handleElementChange(selectedElementIndex, { a: e.target.value })}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #d9d9d9', borderRadius: '6px' }}
                                                >
                                                    <option value="left">左对齐</option>
                                                    <option value="center">居中</option>
                                                    <option value="right">右对齐</option>
                                                    <option value="justify">两端对齐</option>
                                                </select>
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>宽度:</label>
                                                <InputNumber
                                                    value={selectedElement.w}
                                                    onChange={value => handleElementChange(selectedElementIndex, { w: value })}
                                                    style={{ width: '100%' }}
                                                    precision={0}
                                                    min={1}
                                                />
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>高度:</label>
                                                <InputNumber
                                                    value={selectedElement.h}
                                                    onChange={value => handleElementChange(selectedElementIndex, { h: value })}
                                                    style={{ width: '100%' }}
                                                    precision={0}
                                                    min={1}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ marginBottom: '16px' }}>
                                        <label>X坐标:</label>
                                        <InputNumber
                                            value={selectedElement.x}
                                            onChange={value => handleElementChange(selectedElementIndex, { x: value })}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div style={{ marginBottom: '16px' }}>
                                        <label>Y坐标:</label>
                                        <InputNumber
                                            value={selectedElement.y}
                                            onChange={value => handleElementChange(selectedElementIndex, { y: value })}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className={styles.layerControls} style={{ marginTop: 16 }}>
                                        <div className={styles.layerButtonGroup}>
                                            <Button
                                                type="secondary"
                                                onClick={() => moveElementToTop(selectedElementIndex)}
                                                disabled={selectedElementIndex === posterConfig.content.length - 1}
                                            >
                                                移到顶层
                                            </Button>
                                            <Button
                                                type="secondary"
                                                onClick={() => moveElementToBottom(selectedElementIndex)}
                                                disabled={selectedElementIndex === 0}
                                            >
                                                移到底层
                                            </Button>
                                        </div>
                                        <Button
                                            type="danger"
                                            onClick={() => handleDeleteElement(selectedElementIndex)}
                                            className={styles.deleteButton}
                                        >
                                            删除元素
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        )}
                        <Card title="海报背景设置" style={{ marginTop: '16px' }}>
                            <div style={{ padding: '20px' }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <label>画布尺寸锁定:</label>
                                    <Switch
                                        checked={canvasLocked}
                                        onChange={(checked) => setCanvasLocked(checked)}
                                        style={{ marginLeft: '10px' }}
                                    />
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                        更换背景图片不会自动调整画布尺寸
                                    </div>
                                </div>
                                <div style={{ marginBottom: '16px' }}>
                                    <label>背景图片:</label>
                                    <ImageSelector
                                        value={posterConfig.image}
                                        onChange={value => {
                                            handlePosterChange({ image: value });
                                            if (value) {
                                                autoResizeCanvasToImage(value);
                                            }
                                        }}
                                    />
                                    {posterConfig.image && (
                                        <Button
                                            type="danger"
                                            size="small"
                                            onClick={() => {
                                                Modal.confirm({
                                                    title: '确认删除',
                                                    content: '确定要删除背景图片吗？',
                                                    onOk: () => {
                                                        handlePosterChange({ image: "" });
                                                        Toast.success('背景图片已删除');
                                                    },
                                                });
                                            }}
                                            style={{ marginTop: '8px', width: '100%' }}
                                        >
                                            删除背景图
                                        </Button>
                                    )}
                                </div>
                                <div style={{ marginBottom: '16px' }}>
                                    <label>背景颜色:</label>
                                    <ColorPicker
                                        value={posterConfig.bg}
                                        onChange={value => handlePosterChange({ bg: value })}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div style={{ marginBottom: '16px' }}>
                                    <label>宽度:</label>
                                    <InputNumber
                                        value={posterConfig.w}
                                        onChange={value => handlePosterChange({ w: value })}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div style={{ marginBottom: '16px' }}>
                                    <label>高度:</label>
                                    <InputNumber
                                        value={posterConfig.h}
                                        onChange={value => handlePosterChange({ h: value })}
                                        style={{ width: '100%' }}
                                    />
                                </div>

                            </div>
                        </Card>
                    </Sider>
                </Layout>
            </Layout>
        </DndProvider>
    );
};

export default PosterEditor; 